import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { clientsApi } from '../utils/api';

const api = axios.create({ baseURL: '/api', withCredentials: true });

const PHASE = { IDLE: 'idle', DETECTING: 'detecting', CONFIRMING_COLS: 'confirming_cols', GENERATING: 'generating', DONE: 'done', ERROR: 'error' };

function LogBox({ logs }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [logs]);
  return (
    <div ref={ref} style={{
      background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
      padding: 12, fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)',
      height: 180, overflowY: 'auto', marginTop: 12,
    }}>
      {logs.length === 0 ? <span style={{ color: 'var(--text-muted)' }}>Waiting...</span> : logs.map((l, i) => <div key={i}>{l}</div>)}
    </div>
  );
}

export default function PitchGenerator() {
  const [phase, setPhase] = useState(PHASE.IDLE);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [sheetTabs, setSheetTabs] = useState([]);
  const [selectedTab, setSelectedTab] = useState('');
  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [headerRow, setHeaderRow] = useState('1');
  const [file, setFile] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [sessionJobId, setSessionJobId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [detected, setDetected] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [rowFrom, setRowFrom] = useState('');
  const [rowTo, setRowTo] = useState('');
  const [tabsLoading, setTabsLoading] = useState(false);
  const pollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    clientsApi.getAll().then(r => setClients((r.data || []).filter(c => !c.archived)));
  }, []);

  // When client changes, load their spreadsheet ID and tabs
  useEffect(() => {
    if (!selectedClientId) { setSheetTabs([]); setSelectedTab(''); setSpreadsheetId(''); return; }
    const client = clients.find(c => c.id === selectedClientId);
    if (!client?.pitchSheetId) { setSheetTabs([]); setSelectedTab(''); setSpreadsheetId(''); return; }
    setSpreadsheetId(client.pitchSheetId);
    setTabsLoading(true);
    api.get(`/pitching/tabs/${selectedClientId}`)
      .then(r => {
        const tabs = r.data?.tabs || [];
        setSheetTabs(tabs);
        if (tabs.length) setSelectedTab(tabs[0]);
      })
      .catch(() => setSheetTabs([]))
      .finally(() => setTabsLoading(false));
  }, [selectedClientId, clients]);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => () => stopPolling(), []);

  const poll = useCallback((id, onDone) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/pitch-generator/status/${id}`);
        const job = res.data;
        setLogs(job.logs || []);
        if (job.status === 'done') {
          stopPolling();
          onDone(job.result);
        } else if (job.status === 'error') {
          stopPolling();
          setError(job.error || 'An error occurred.');
          setPhase(PHASE.ERROR);
        }
      } catch (_) {}
    }, 2000);
  }, []);

  const clientName = clients.find(c => c.id === selectedClientId)?.name || '';

  const handleDetect = async () => {
    if (!file || !spreadsheetId || !selectedTab) return;
    setPhase(PHASE.DETECTING);
    setLogs([]);
    setError('');
    setDetected(null);

    const form = new FormData();
    form.append('package', file);
    form.append('spreadsheetId', spreadsheetId);
    form.append('sheetName', selectedTab);
    form.append('clientName', clientName);
    form.append('clientId', selectedClientId);
    form.append('headerRow', headerRow);
    if (rowFrom) form.append('rowFrom', rowFrom);
    if (rowTo) form.append('rowTo', rowTo);

    try {
      const res = await api.post('/pitch-generator/detect', form);
      const id = res.data.jobId;
      setJobId(id);
      poll(id, (result) => {
        if (result?.phase === 'awaiting_column_confirm') {
          setDetected(result.detected);
          setSessionJobId(id);
          setPhase(PHASE.CONFIRMING_COLS);
        } else if (result?.phase === 'missing_pitch_column') {
          setError(result.message || "No 'Pitch' column found.");
          setPhase(PHASE.ERROR);
        } else {
          setError('Unexpected response.');
          setPhase(PHASE.ERROR);
        }
      });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setPhase(PHASE.ERROR);
    }
  };

  const handleGenerate = async () => {
    setPhase(PHASE.GENERATING);
    setLogs([]);
    try {
      const res = await api.post('/pitch-generator/generate', { sessionJobId });
      const id = res.data.jobId;
      setJobId(id);
      poll(id, (r) => { setResult(r); setPhase(PHASE.DONE); });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setPhase(PHASE.ERROR);
    }
  };

  const reset = () => { setPhase(PHASE.IDLE); setFile(null); setDetected(null); setResult(null); setError(''); setLogs([]); };

  const s = { card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24 } };
  const label = { fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 };
  const input = { width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', color: 'var(--text-primary)', fontSize: 13 };

  return (
    <div style={{ padding: 32, maxWidth: 800, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, marginBottom: 6 }}>Pitch Generator</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          Select a client and sheet tab, upload a client package, and the tool will generate personalized pitches for every podcast that doesn't have one yet — then write them directly back to your Google Sheet.
        </p>
      </div>

      {/* ── Setup form ── */}
      {(phase === PHASE.IDLE || phase === PHASE.ERROR) && (
        <div style={s.card}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={label}>Client</label>
              <select value={selectedClientId} onChange={e => setSelectedClientId(e.target.value)} style={{ ...input, cursor: 'pointer' }}>
                <option value="">— Select a client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {selectedClientId && !spreadsheetId && (
              <div style={{ color: 'var(--amber)', fontSize: 13, background: 'var(--amber-soft)', borderRadius: 'var(--radius-sm)', padding: '10px 14px' }}>
                This client has no pitch sheet linked. Add their Sheet ID on the client profile first.
              </div>
            )}

            {spreadsheetId && (
              <div>
                <label style={label}>Sheet Tab</label>
                {tabsLoading
                  ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading tabs...</div>
                  : sheetTabs.length
                    ? <select value={selectedTab} onChange={e => setSelectedTab(e.target.value)} style={{ ...input, cursor: 'pointer' }}>
                        {sheetTabs.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    : <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No tabs found — sync the client first.</div>
                }
              </div>
            )}

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: '0 0 auto' }}>
                <label style={label}>Header Row Number</label>
                <input type="number" min="1" value={headerRow} onChange={e => setHeaderRow(e.target.value)} style={{ ...input, width: 120 }} />
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label style={label}>Row Range <span style={{ textTransform: 'none', letterSpacing: 0, color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="number" min="1" value={rowFrom} onChange={e => setRowFrom(e.target.value)} placeholder="From row" style={{ ...input, width: 110 }} />
                  <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>to</span>
                  <input type="number" min="1" value={rowTo} onChange={e => setRowTo(e.target.value)} placeholder="To row" style={{ ...input, width: 110 }} />
                </div>
              </div>
            </div>

            <div>
              <label style={label}>Client Package</label>
              <input ref={fileRef} type="file" accept=".txt,.pdf,.docx,.md,.csv" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
              <button className="btn btn-ghost" onClick={() => fileRef.current.click()} style={{ marginRight: 12 }}>
                {file ? file.name : 'Choose file...'}
              </button>
              {file && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{(file.size / 1024).toFixed(1)} KB</span>}
            </div>

            {error && <div style={{ color: 'var(--red)', fontSize: 13, background: 'var(--red-soft)', borderRadius: 'var(--radius-sm)', padding: '10px 14px' }}>{error}</div>}

            <button className="btn btn-primary" disabled={!file || !spreadsheetId || !selectedTab} onClick={handleDetect} style={{ alignSelf: 'flex-start' }}>
              Detect Columns
            </button>
          </div>
        </div>
      )}

      {/* ── Detecting ── */}
      {phase === PHASE.DETECTING && (
        <div style={s.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', animation: 'pulse 1.5s infinite' }} />
            <span style={{ fontWeight: 500 }}>Reading sheet structure...</span>
          </div>
          <LogBox logs={logs} />
        </div>
      )}

      {/* ── Column confirmation ── */}
      {phase === PHASE.CONFIRMING_COLS && detected && (
        <div style={s.card}>
          <h2 style={{ fontSize: 16, marginBottom: 4 }}>Detected Columns</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20 }}>
            Confirm the columns detected below are correct, then click Generate to start writing pitches to your sheet.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
            {Object.entries(detected).map(([key, val]) => (
              <div key={key} style={{ background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{key}</div>
                <div style={{ fontSize: 13, color: val ? 'var(--text-primary)' : 'var(--text-muted)' }}>{val || '— not detected —'}</div>
              </div>
            ))}
          </div>

          <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 20, background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: '10px 14px' }}>
            Pitches will be generated for rows that have a Name and Email but no existing pitch text. Rows with status: booked, answered, pitched, inactive, or invalid are skipped.
            {(rowFrom || rowTo) && (
              <span style={{ display: 'block', marginTop: 6, color: 'var(--accent)' }}>
                Row range filter active: rows {rowFrom || '1'} – {rowTo || 'end'}.
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-ghost" onClick={reset}>Start over</button>
            <button className="btn btn-primary" onClick={handleGenerate}>Generate Pitches</button>
          </div>
        </div>
      )}

      {/* ── Generating ── */}
      {phase === PHASE.GENERATING && (
        <div style={s.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', animation: 'pulse 1.5s infinite' }} />
            <span style={{ fontWeight: 500 }}>Generating pitches...</span>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Writing pitches directly to your Google Sheet as they're generated.</p>
          <LogBox logs={logs} />
        </div>
      )}

      {/* ── Done ── */}
      {phase === PHASE.DONE && result && (
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
            <h2 style={{ fontSize: 18, marginBottom: 8 }}>
              {result.generated > 0 ? `${result.generated} pitch${result.generated !== 1 ? 'es' : ''} written to sheet!` : 'Done'}
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20 }}>
              {result.message || `${result.generated} pitches generated${result.failed ? `, ${result.failed} failed` : ''}.`}
            </p>
            <button className="btn btn-primary" onClick={reset}>Run another</button>
          </div>
        </div>
      )}
    </div>
  );
}
